import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  BodyLink,
  BodyText,
  hexToRGB,
  Spinner,
  Message
} from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { useForm } from 'react-hook-form';
import CheckBoxWithControl from '~/App/shared/components/Fields/CheckBoxWithControl';
import {
  CompanySubscription,
  isSubscriptionType,
  SubscriptionType
} from '~/App/shared/interfaces/EmailSubscription';
import {
  getSubscriptions,
  updateSubscriptions
} from '~/helpers/orchestration/subscribe';
import { useAsync } from '~/App/shared/hooks/useAsync';
import { useSelector } from 'react-redux';
import { idToken as idTokenSelector } from '~/App/shared/selectors/sessionSelector';
import { AsyncStatus } from '~/App/shared/types/Async';
import StatusPageError from '~/App/shared/components/StatusPageError';
import StatusPageLoading from '~/App/shared/components/StatusPageLoading';
import { EmailSubscriptionsData } from '~/App/shared/interfaces/store/CmsData';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  text-align: left;
  position: relative;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
`;

const UpdatingOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => hexToRGB(theme.colors.background.light, 0.8)};
  z-index: 10;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CheckboxWrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const WrappedMessage = styled(Message)`
  margin-top: 0.5rem;
`;

type Props = {
  hash?: string;
  subscriptions: EmailSubscriptionsData['subscriptions'];
};

const companyFieldName = (customerNumber: number, type: SubscriptionType) =>
  `${customerNumber}:${type}`;

const parseCompanyFieldName = (
  name: string
): { customerNumber: number; type: SubscriptionType } | null => {
  const [customerNumberPart, typePart] = name.split(':');
  if (!isSubscriptionType(typePart)) return null;
  return { customerNumber: Number(customerNumberPart), type: typePart };
};

const SubscriptionManagementForm = ({ hash, subscriptions }: Props) => {
  const { t } = useTranslation();
  const idToken = useSelector(idTokenSelector);
  const [subscriptionInUpdate, setSubscriptionInUpdate] = useState<
    string | null
  >(null);
  const {
    data: existingSubscriptions,
    status,
    error
  } = useAsync({
    fetcher: getSubscriptions,
    fetcherArgs: [{ idToken, hash }]
  });
  const {
    control,
    watch,
    reset,
    setError,
    formState: { errors },
    clearErrors,
    setValue
  } = useForm<Record<string, boolean>>();
  const [supportedSubscriptionTypes, optInSubscriptions] = useMemo(() => {
    if (existingSubscriptions) {
      const allExistingSubscriptions = [
        ...existingSubscriptions.optIn,
        ...existingSubscriptions.optOut
      ];
      const allCmsSubscriptions: SubscriptionType[] =
        subscriptions
          ?.map(subscription => subscription.subscriptionType)
          .filter(isSubscriptionType) ?? [];
      return [
        allCmsSubscriptions.filter(subscription =>
          allExistingSubscriptions.includes(subscription)
        ),
        existingSubscriptions.optIn
      ];
    }
    return [[], []];
  }, [existingSubscriptions, subscriptions]);

  const companyGroups: CompanySubscription[] = useMemo(() => {
    const allCmsSubscriptions: SubscriptionType[] =
      subscriptions
        ?.map(subscription => subscription.subscriptionType)
        .filter(isSubscriptionType) ?? [];
    return (existingSubscriptions?.companySubscriptions ?? [])
      .map(group => ({
        ...group,
        optIn: group.optIn.filter(type => allCmsSubscriptions.includes(type)),
        optOut: group.optOut.filter(type => allCmsSubscriptions.includes(type))
      }))
      .filter(group => group.optIn.length + group.optOut.length > 0);
  }, [existingSubscriptions, subscriptions]);

  const isFetching = status === AsyncStatus.pending;
  const isError = Boolean(error);

  useEffect(() => {
    const subscription = watch(async (value, { name, type }) => {
      if (name && type === 'change') {
        const fieldValue = value?.[name];
        clearErrors();
        setSubscriptionInUpdate(name);

        const companyField = parseCompanyFieldName(name);

        try {
          if (companyField) {
            await updateSubscriptions({
              optIn: [],
              optOut: [],
              companySubscriptions: [
                {
                  customerNumber: companyField.customerNumber,
                  optIn: fieldValue ? [companyField.type] : [],
                  optOut: !fieldValue ? [companyField.type] : []
                }
              ],
              idToken,
              hash
            });
          } else if (isSubscriptionType(name)) {
            await updateSubscriptions({
              optIn: fieldValue ? [name] : [],
              optOut: !fieldValue ? [name] : [],
              idToken,
              hash
            });
          }
        } catch (e) {
          setError(name, { type: 'manual' });
          setValue(name, !fieldValue);
        }

        setSubscriptionInUpdate(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setError, idToken, clearErrors, setValue, hash]);

  useEffect(() => {
    if (supportedSubscriptionTypes?.length || companyGroups.length) {
      const defaults: Record<string, boolean> = {};
      for (const current of supportedSubscriptionTypes) {
        defaults[current] = optInSubscriptions.includes(current);
      }
      for (const group of companyGroups) {
        for (const optInType of group.optIn) {
          defaults[companyFieldName(group.customerNumber, optInType)] = true;
        }
        for (const optOutType of group.optOut) {
          defaults[companyFieldName(group.customerNumber, optOutType)] = false;
        }
      }
      reset(defaults);
    }
  }, [reset, supportedSubscriptionTypes, optInSubscriptions, companyGroups]);

  return (
    <>
      {isFetching && <StatusPageLoading minHeight={0} />}
      {isError && <StatusPageError minHeight={0} />}
      {!isFetching && !isError && (
        <Form noValidate>
          {supportedSubscriptionTypes.map(subscriptionType => {
            const subscriptionCmsData = subscriptions?.find(
              subscription => subscription.subscriptionType === subscriptionType
            );
            return (
              <CheckboxWrapper key={subscriptionType}>
                {subscriptionType === subscriptionInUpdate && (
                  <UpdatingOverlay>
                    <Spinner
                      color="neutral"
                      speed="fast"
                      size={subscriptionCmsData?.checkboxDescription ? 2 : 1}
                    />
                  </UpdatingOverlay>
                )}
                <CheckBoxWithControl
                  data-testid={`subscription-${subscriptionType}`}
                  control={control}
                  name={subscriptionType}
                  label={
                    <>
                      <BodyLink as="span">
                        {subscriptionCmsData?.checkboxLabel}
                      </BodyLink>
                      <BodyText>
                        {subscriptionCmsData?.checkboxDescription}
                      </BodyText>
                    </>
                  }
                  color="info"
                  isDisabled={isError}
                />
                {errors?.[subscriptionType] && (
                  <WrappedMessage type="error" withIcon isDeletable>
                    {t('Something went wrong, please try again later.')}
                  </WrappedMessage>
                )}
              </CheckboxWrapper>
            );
          })}
          {companyGroups.flatMap(group =>
            [...group.optIn, ...group.optOut].map(type => {
              const fieldName = companyFieldName(group.customerNumber, type);
              const subscriptionCmsData = subscriptions?.find(
                subscription => subscription.subscriptionType === type
              );
              return (
                <CheckboxWrapper key={fieldName}>
                  {fieldName === subscriptionInUpdate && (
                    <UpdatingOverlay>
                      <Spinner
                        color="neutral"
                        speed="fast"
                        size={subscriptionCmsData?.checkboxDescription ? 2 : 1}
                      />
                    </UpdatingOverlay>
                  )}
                  <CheckBoxWithControl
                    data-testid={`subscription-${fieldName}`}
                    control={control}
                    name={fieldName}
                    label={
                      <>
                        <BodyLink as="span">
                          {group.organisationName} -{' '}
                          {subscriptionCmsData?.checkboxLabel}
                        </BodyLink>
                        <BodyText>
                          {subscriptionCmsData?.checkboxDescription}
                        </BodyText>
                      </>
                    }
                    color="info"
                    isDisabled={isError}
                  />
                  {errors?.[fieldName] && (
                    <WrappedMessage type="error" withIcon isDeletable>
                      {t('Something went wrong, please try again later.')}
                    </WrappedMessage>
                  )}
                </CheckboxWrapper>
              );
            })
          )}
        </Form>
      )}
    </>
  );
};

export default SubscriptionManagementForm;
